import Tabs from "../../components/General/ResponsiveTabs/ResponsiveTabs";
import { ChangeEvent, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import useAuth from "../../hooks/useAuth";
import {
  BlacklistScope,
  blacklistError,
  BrokerBlacklistEntry,
  BrokerBlacklistWrite,
  brokerBlacklistService,
  ImportPreview,
  ImportResult,
} from "../../api/brokerBlacklistService";
import BrokerBlacklistDetails from "../../components/Operations/BrokerBlacklist/BrokerBlacklistDetails";
import CheckBrokerButton from "../../components/Operations/BrokerBlacklist/CheckBrokerButton";

const columns = ["Name", "Address", "Email", "Domain", "MC", "DOT"];
const values = (entry: BrokerBlacklistWrite) => [
  entry.name,
  entry.address,
  entry.email,
  entry.domain,
  entry.mcNumber,
  entry.dotNumber,
];
const fields: {
  key: keyof BrokerBlacklistWrite;
  label: string;
  limit: number;
}[] = [
  { key: "name", label: "Name", limit: 300 },
  { key: "address", label: "Address", limit: 1000 },
  { key: "email", label: "Email", limit: 320 },
  { key: "domain", label: "Domain", limit: 253 },
  { key: "mcNumber", label: "MC number", limit: 32 },
  { key: "dotNumber", label: "DOT number", limit: 32 },
  { key: "reason", label: "Blacklist reason", limit: 4000 },
  {
    key: "sourceEmail",
    label: "Source email (paste message or reference)",
    limit: 20000,
  },
];

export default function BrokerBlacklistPage() {
  const { token, role } = useAuth();
  const client = useQueryClient();
  const canManage = role?.some(
    (r) => r === "OperationsManager" || r === "OperationsAdmin",
  );
  const [list, setList] = useState<BlacklistScope>("C1");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<BrokerBlacklistWrite>();
  const [editId, setEditId] = useState<string>();
  const [deleting, setDeleting] = useState<BrokerBlacklistEntry | null>(null);
  const [details, setDetails] = useState<BrokerBlacklistEntry | null>(null);
  const [preview, setPreview] = useState<{
    list: BlacklistScope;
    data: ImportPreview;
    filename: string;
  }>();
  const [reading, setReading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<ImportResult>();
  const query = useQuery({
    queryKey: ["brokerBlacklist", token, list],
    enabled: !!token,
    queryFn: () => brokerBlacklistService.get(token!, list),
  });
  const entries = useMemo(
    () =>
      (query.data || []).filter((entry) =>
        [...values(entry), entry.reason, entry.sourceEmail].some((v) =>
          v?.toLowerCase().includes(search.trim().toLowerCase()),
        ),
      ),
    [query.data, search],
  );
  const refresh = () =>
    client.invalidateQueries({ queryKey: ["brokerBlacklist"] });
  const save = useMutation({
    mutationFn: () => brokerBlacklistService.save(token!, form!, editId),
    onSuccess: () => {
      setForm(undefined);
      void refresh();
    },
    onError: (e) => setError(blacklistError(e)),
  });
  const remove = useMutation({
    mutationFn: () => brokerBlacklistService.remove(token!, deleting!.id),
    onSuccess: () => {
      setDeleting(null);
      void refresh();
    },
    onError: (e) => setError(blacklistError(e)),
  });
  const importRows = useMutation({
    mutationFn: () =>
      brokerBlacklistService.import(
        token!,
        preview!.list,
        preview!.data.entries,
      ),
    onSuccess: (data) => {
      setResult(data);
      setPreview(undefined);
      void refresh();
    },
    onError: (e) => setError(blacklistError(e)),
  });
  const readFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !token) return;
    if (
      !file.name.toLowerCase().endsWith(".xlsx") ||
      file.size > 10 * 1024 * 1024
    ) {
      setError("Choose an .xlsx file up to 10 MB.");
      return;
    }
    setReading(true);
    setError("");
    setResult(undefined);
    const selectedList = list;
    try {
      setPreview({
        list: selectedList,
        data: await brokerBlacklistService.preview(token, selectedList, file),
        filename: file.name,
      });
    } catch (e) {
      setError(blacklistError(e));
    } finally {
      setReading(false);
    }
  };
  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Typography variant="h5">Broker Blacklist</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <CheckBrokerButton />
          {canManage && (
            <>
              <Button
                variant="outlined"
                component="label"
                disabled={reading || importRows.isPending}
              >
                {reading ? "Reading Excel…" : `Import Excel to ${list}`}
                <input hidden type="file" accept=".xlsx" onChange={readFile} />
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setEditId(undefined);
                  setError("");
                  setForm({ list, name: "" });
                }}
              >
                Add broker
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      <Tabs
        value={list}
        onChange={(_, value) => {
          setList(value);
          setError("");
          setResult(undefined);
        }}
        aria-label="Broker blacklist filter"
      >
        <Tab value="C1" label="C1 blacklist" />
        <Tab value="Fox" label="Fox blacklist" />
      </Tabs>
      <Typography variant="body2" color="text.secondary">
        C1 and Fox are separate blacklists. Select a list before adding or
        importing brokers.
      </Typography>
      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {result && (
        <Alert severity={result.errors.length ? "warning" : "success"}>
          Imported {result.imported} broker(s). Skipped {result.duplicates}{" "}
          duplicate(s).
          {result.errors.map((message, i) => (
            <Typography key={i} variant="body2">
              {message}
            </Typography>
          ))}
        </Alert>
      )}
      <TextField
        size="small"
        label="Search name, address, email, domain, MC or DOT"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {query.isPending && <CircularProgress aria-label="Loading blacklist" />}
      {query.isError && (
        <Alert
          severity="error"
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        >
          Unable to load the blacklist.
        </Alert>
      )}
      {!query.isPending && !query.isError && (
        <>
          <Typography variant="body2">
            {entries.length} broker(s) in {list}
            {search ? " matching this search" : ""}
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small" aria-label={`${list} blacklist`}>
              <TableHead>
                <TableRow>
                  {[
                    ...columns,
                    "Details",
                    ...(canManage ? ["Actions"] : []),
                  ].map((label) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    {values(entry).map((v, i) => (
                      <TableCell
                        key={i}
                        sx={{ overflowWrap: "anywhere", maxWidth: 300 }}
                      >
                        {v || "—"}
                      </TableCell>
                    ))}
                    <TableCell>
                      <Button onClick={() => setDetails(entry)}>
                        Reason & source email
                      </Button>
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        <Stack
                          flexWrap={{ xs: "wrap", lg: "nowrap" }}
                          useFlexGap
                          direction="row"
                        >
                          <Button
                            onClick={() => {
                              setError("");
                              setEditId(entry.id);
                              setForm({ ...entry });
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            color="error"
                            onClick={() => {
                              setError("");
                              setDeleting(entry);
                            }}
                          >
                            Remove
                          </Button>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {!entries.length && (
                  <TableRow>
                    <TableCell colSpan={canManage ? 8 : 7}>
                      No brokers found in the {list} blacklist.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <BrokerBlacklistDetails
        entry={details}
        onClose={() => setDetails(null)}
      />
      <Dialog
        open={!!form}
        onClose={() => {
          if (!save.isPending) setForm(undefined);
        }}
        fullWidth
        maxWidth="md"
      >
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            setError("");
            save.mutate();
          }}
        >
          <DialogTitle>
            {editId ? "Edit" : "Add"} broker — {form?.list} blacklist
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}
              {fields.map((field) => (
                <Grid
                  item
                  xs={12}
                  sm={
                    field.key === "sourceEmail" || field.key === "reason"
                      ? 12
                      : 6
                  }
                  key={field.key}
                >
                  <TextField
                    fullWidth
                    label={field.label}
                    value={form?.[field.key] || ""}
                    required={field.key === "name"}
                    type={field.key === "email" ? "email" : "text"}
                    multiline={
                      field.key === "sourceEmail" || field.key === "reason"
                    }
                    minRows={
                      field.key === "sourceEmail"
                        ? 4
                        : field.key === "reason"
                          ? 2
                          : undefined
                    }
                    disabled={save.isPending}
                    inputProps={{ maxLength: field.limit }}
                    helperText={
                      field.key === "domain"
                        ? "Business email domains are filled automatically. Shared providers such as gmail.com are not inferred."
                        : undefined
                    }
                    onChange={(e) =>
                      setForm(
                        (previous) =>
                          previous && {
                            ...previous,
                            [field.key]: e.target.value,
                          },
                      )
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={save.isPending}
              onClick={() => setForm(undefined)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={save.isPending || !form?.name.trim()}
            >
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog
        open={!!deleting}
        onClose={() => {
          if (!remove.isPending) setDeleting(null);
        }}
      >
        <DialogTitle>
          Remove broker from {deleting?.list} blacklist?
        </DialogTitle>
        <DialogContent>
          <Typography>{deleting?.name}</Typography>
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={remove.isPending} onClick={() => setDeleting(null)}>
            Cancel
          </Button>
          <Button
            color="error"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!preview}
        onClose={() => {
          if (!importRows.isPending) setPreview(undefined);
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Import to {preview?.list} blacklist</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <Typography>
              {preview?.filename}: {preview?.data.entries.length} valid broker
              row(s). Existing entries will be skipped. Only name, address,
              email, domain, MC and DOT are imported.
            </Typography>
            {!!preview?.data.errors.length && (
              <Alert severity="warning">
                <Typography>
                  {preview.data.errors.length} row or sheet issue(s):
                </Typography>
                <Box sx={{ maxHeight: 160, overflow: "auto" }}>
                  {preview.data.errors.map((message, i) => (
                    <Typography key={i} variant="body2">
                      {message}
                    </Typography>
                  ))}
                </Box>
              </Alert>
            )}
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader aria-label="Import preview">
                <TableHead>
                  <TableRow>
                    {columns.map((label) => (
                      <TableCell key={label}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview?.data.entries.slice(0, 100).map((entry, row) => (
                    <TableRow key={row}>
                      {values(entry).map((value, col) => (
                        <TableCell key={col}>{value || "—"}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {(preview?.data.entries.length || 0) > 100 && (
              <Typography variant="caption">
                Showing the first 100 rows. All valid rows will be imported.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={importRows.isPending}
            onClick={() => setPreview(undefined)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={importRows.isPending || !preview?.data.entries.length}
            onClick={() => importRows.mutate()}
          >
            {importRows.isPending
              ? "Importing…"
              : `Import ${preview?.data.entries.length || 0} broker(s) to ${preview?.list}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
